import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useCreateLevelEntity } from '../../../../hooks/useLevelEntities';
import { handleApiError, resolveErrorMessage } from '../../../../api/errorMapping';
import { ApiError } from '../../../../api/errors';
import { Button, HelpTooltip } from '@fiado/react-kit';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import type { LevelNameLocal } from '../../../../api/types/LevelEntityLocal';

// DEC-RBAC-027: charset del id BYOID, espejo de `requireValidScopeId` del backend (1..128, [A-Za-z0-9_-]).
const ENTITY_ID_REGEX = /^[A-Za-z0-9_-]+$/;

/**
 * Schema condicional al BYOID del nivel. Cuando el nivel es externo (`requireExternalId`), el campo
 * `id` es REQUERIDO y validado (espejo backend); cuando no, el campo no existe (no se muestra, no se
 * valida, no se manda → el backend genera UUID). Espejo de `GroupForm`.
 */
function buildSchema(requireExternalId: boolean) {
    const base = { displayName: z.string().min(1).max(120) };
    if (!requireExternalId) return z.object(base);
    return z.object({
        ...base,
        id: z
            .string()
            .min(1, 'El ID externo es obligatorio')
            .max(128, 'Máximo 128 caracteres')
            .regex(ENTITY_ID_REGEX, 'Solo letras, números, guion y guion bajo'),
    });
}

type FormValues = { displayName: string; id?: string };

interface Props {
    /** tenantId del DRILL-DOWN (param de ruta). La entidad se crea en ESTE tenant. */
    tenantId: string;
    /** Nivel sobre el que se crea (LEVEL_1/2/3). */
    level: LevelNameLocal;
    /** Nombre legible del nivel (de la config de la app del tenant). Para labels — NUNCA "LEVEL_1" crudo. */
    levelDisplayName: string;
    /**
     * DEC-RBAC-027: cuando el nivel es BYOID (externo), se muestra y exige el campo "ID" y se manda al
     * backend. Default `false` (BYOID apagado → el backend genera UUID).
     */
    requireExternalId?: boolean;
    /**
     * Padre del nivel inmediato superior. OBLIGATORIO para LEVEL_2/LEVEL_3 (lo inyecta el caller desde
     * el filtro/selector de padre). Ausente para LEVEL_1.
     */
    parentId?: string;
    /** Se llama tras crear OK (ej. cerrar el modal del padre). */
    onDone?: () => void;
}

/**
 * Alta de una entidad de nivel genérica. Espeja `GroupForm`/`PlatformRetailerForm` (react-hook-form +
 * zod + mutation con invalidación), pero parametrizado por `level` y con el `displayName` del nivel
 * (de la app del tenant) en los labels. El backend exige `parentId` en LEVEL_2/LEVEL_3.
 */
export function LevelEntityForm({
    tenantId,
    level,
    levelDisplayName,
    requireExternalId = false,
    parentId,
    onDone,
}: Props) {
    const form = useForm<FormValues>({
        resolver: zodResolver(buildSchema(requireExternalId)),
        defaultValues: { displayName: '', id: '' },
    });

    const mut = useCreateLevelEntity(tenantId, level);

    const submit = (v: FormValues) => {
        mut.mutate(
            {
                displayName: v.displayName,
                // El id externo SOLO viaja cuando BYOID está prendido; si no, el backend genera UUID.
                ...(requireExternalId ? { id: v.id } : {}),
                // parentId obligatorio para LEVEL_2/3; ausente en LEVEL_1.
                ...(parentId ? { parentId } : {}),
            },
            {
                onSuccess: () => {
                    toast.success(`${levelDisplayName} creado`);
                    if (onDone) onDone();
                },
                onError: (err) => {
                    // Colisión de id BYOID: 409 SCOPE_ID_ALREADY_EXISTS → al campo del id (espejo GroupForm).
                    if (
                        requireExternalId &&
                        err instanceof ApiError &&
                        err.code === 'SCOPE_ID_ALREADY_EXISTS'
                    ) {
                        form.setError('id', {
                            message: `Ya existe un registro con el ID externo "${form.getValues('id') ?? ''}". Elige otro.`,
                        });
                        return;
                    }
                    const strategy = handleApiError(err);
                    if (strategy === 'INLINE' && err instanceof ApiError) {
                        form.setError('displayName', { message: resolveErrorMessage(err) });
                    }
                },
            },
        );
    };

    return (
        <form
            onSubmit={(e) => {
                void form.handleSubmit(submit)(e);
            }}
            className="space-y-4"
        >
            {requireExternalId && (
                <div className="space-y-2">
                    <div className="flex items-center gap-1">
                        <Label htmlFor="lef-id">ID externo</Label>
                        <HelpTooltip content="Identificador de este registro en tu sistema. Se pide porque este nivel está configurado con ID externo obligatorio." />
                    </div>
                    <Input id="lef-id" {...form.register('id')} />
                    <p className="text-sm text-muted-foreground">
                        El identificador con el que tu sistema reconoce este registro. No se puede
                        cambiar después.
                    </p>
                    {form.formState.errors.id && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.id.message}
                        </p>
                    )}
                </div>
            )}
            <div className="space-y-2">
                <Label htmlFor="lef-name">Nombre</Label>
                <Input id="lef-name" {...form.register('displayName')} />
                {form.formState.errors.displayName && (
                    <p className="text-sm text-destructive">
                        {form.formState.errors.displayName.message}
                    </p>
                )}
            </div>
            <Button type="submit" isLoading={mut.isPending}>
                Crear
            </Button>
        </form>
    );
}
